/**
 * 아이디를 직접 짓지 않고도, 화면 안에서 겹치지 않는 고유 id를 만들어야 할 때 쓴다.
 * 주로 label과 input을 htmlFor/id로 잇거나, aria-labelledby처럼 접근성 속성에 쓸 id가 필요할 때 쓴다.
 * React.useId를 감싼 것이라 서버 렌더링과 클라이언트 렌더링 결과가 항상 같아서, 하이드레이션
 * (서버가 만든 HTML에 클라이언트 React를 붙이는 과정) 시 id가 어긋나는 문제가 생기지 않는다.
 */
// This implementation is heavily inspired by react-aria's implementation
import * as React from 'react';

/**
 * idProp을 넘기면 그 값을 그대로 쓰고, 없으면 prefix를 붙인 자동 생성 id를 반환한다.
 */
export function useId(idProp?: string, prefix?: string): string {
  const id = React.useId();

  return React.useMemo(
    () => idProp || [prefix, id].filter(Boolean).join('-'),
    [idProp, prefix, id],
  );
}

/**
 * React hook to generate ids for use in compound components
 *
 * @param idProp the external id passed from the user
 * @param prefixes array of prefixes to use
 *
 * @example
 *
 * ```js
 * const [buttonId, menuId] = useIds("52", "button", "menu")
 *
 * // buttonId will be `button-52`
 * // menuId will be `menu-52`
 * ```
 */
export function useIds(idProp?: string, ...prefixes: string[]) {
  const id = useId(idProp);
  return React.useMemo(() => {
    return prefixes.map((prefix) => `${prefix}-${id}`);
  }, [id, prefixes]);
}

/**
 * Used to generate an id, and after render, check if that id is rendered, so we know
 * if we can use it in places such as `aria-labelledby`.
 *
 * @param partId - The unique id for the component part
 *
 * @example
 * const { ref, id } = useOptionalPart<HTMLInputElement>(`${id}-label`)
 */
export function useOptionalPart<T = any>(partId: string) {
  const [id, setId] = React.useState<string | null>(null);
  const ref = React.useCallback(
    (node: T) => {
      setId(node ? partId : null);
    },
    [partId],
  );
  return { ref, id, isRendered: Boolean(id) };
}
